// Reach and engagement rate for the active filters. Reach is each platform's
// own period reach; totals over several platforms (or brands, or markets) are
// the sum of those figures: nobody can deduplicate a person across Meta,
// TikTok and Reddit, so that sum is the honest total — never a sum over days.
// ER is engagement ÷ impressions (Prado's choice, 25 Sep 2026), from the
// stored daily counters, so it does not depend on a platform's reach limits.
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "@/lib/filters";
import { useReference } from "@/lib/use-reference";
import { getAwarenessFacts, getPeriodReach, type AwarenessFact, type ReachRow } from "@/lib/api/reach";
import type { ActivityFilter, ActivityType } from "@/lib/api/types";

export interface ReachValue {
  /** Sum of the platforms' own period reach over the selected parts that have one. */
  reach: number | null;
  /** Engagements and impressions of the platforms that report engagement (not
   *  Reddit, Google, Bing): ER = engagements ÷ impressions. */
  engagements: number;
  impressions: number;
  er: number | null;
  /** Every part with delivery had a reach figure from its platform. */
  complete: boolean;
  /** …and every figure was deduplicated by the platform, not added up. */
  exact: boolean;
  /** Why a part is missing or not deduplicated, for a tooltip. */
  notes: string[];
  /** Platforms with delivery but no reach figure for this period. */
  missing: string[];
}

export type ReachDim = "total" | "platform" | "brand" | "region";

const PLATFORM_NAME: Record<string, string> = { meta: "Meta", instagram: "Instagram", tiktok: "TikTok", reddit: "Reddit", linkedin: "LinkedIn", google: "Google", bing: "Bing" };

/** Why a reach-based figure is partial or not deduplicated, one sentence each. */
export function reachCaveats(v: ReachValue | undefined): string[] {
  if (!v || v.exact) return [];
  const out = [...v.notes];
  if (v.missing.length && v.reach != null) out.unshift(`Zonder ${v.missing.map((p) => PLATFORM_NAME[p] ?? p).join(" en ")}.`);
  return out;
}

const DAY = 86_400_000;
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);

function previousWindow(from: string, to: string): [string, string] {
  const a = new Date(from).getTime();
  const len = Math.round((new Date(to).getTime() - a) / DAY) + 1;
  return [iso(a - len * DAY), iso(a - DAY)];
}

function inActivity(activity: ActivityFilter, row: ActivityType): boolean {
  if (activity === "all") return true;
  if (activity === "non_ecom") return row !== "ecom";
  return row === activity;
}

function usePeriod(from: string, to: string, enabled: boolean) {
  const reach = useQuery({
    queryKey: ["period-reach", from, to],
    queryFn: () => getPeriodReach(from, to),
    enabled,
    staleTime: 10 * 60_000,
    refetchInterval: (q) => (q.state.data?.status === "pending" ? 3000 : false),
    throwOnError: false,
  });
  const facts = useQuery({
    queryKey: ["awareness-facts", from, to],
    queryFn: () => getAwarenessFacts(from, to),
    enabled,
    throwOnError: false,
  });
  return { reach, facts };
}

/** Platforms whose API reports an engagement count. Engagement rate is only
 *  defined for these: impressions of Reddit, Google or Bing must not dilute it. */
const ENGAGEMENT_PLATFORMS = new Set(["meta", "instagram", "tiktok", "linkedin"]);

function aggregate(parts: ReachRow[], facts: AwarenessFact[]): ReachValue | undefined {
  const rated = facts.filter((x) => ENGAGEMENT_PLATFORMS.has(x.platform));
  if (!parts.length && !rated.some((x) => x.impressions > 0)) return undefined;
  const known = parts.filter((p) => p.reach != null);
  const reach = known.length ? known.reduce((s, p) => s + (p.reach ?? 0), 0) : null;
  const engagements = rated.reduce((s, x) => s + x.engagements, 0);
  const impressions = rated.reduce((s, x) => s + x.impressions, 0);
  const notes = [...new Set(parts.filter((p) => !p.exact && p.note).map((p) => p.note!))];
  const complete = known.length === parts.length;
  const missing = [...new Set(parts.filter((p) => p.reach == null).map((p) => p.platform))];
  return {
    reach, engagements, impressions, notes, complete, missing,
    er: impressions ? engagements / impressions : null,
    exact: complete && parts.every((p) => p.exact),
  };
}

/** Exact-per-platform reach for the active filters, grouped on demand.
 *  `activity` pins the activity (pages fixed to awareness); `allMarkets` ignores
 *  the market filter (Markten shows every market side by side); `previous`
 *  reads the equal-length window before the selected one. */
export function useExactReach(opts: { activity?: ActivityFilter; allMarkets?: boolean; previous?: boolean; enabled?: boolean } = {}) {
  const f = useFilters();
  const { brands, platforms } = useReference();
  const [from, to] = opts.previous ? previousWindow(f.fromDate, f.toDate) : [f.fromDate, f.toDate];
  const { reach, facts } = usePeriod(from, to, opts.enabled ?? true);
  const activity = opts.activity ?? f.activity;
  const channelKey = f.channel === "all" ? null : platforms.find((p) => p.id === f.channel)?.key ?? f.channel;
  const brandKey = new Map(brands.map((b) => [b.id, b.key]));

  const ready = reach.data?.status === "ready" && !!facts.data;
  const scopeOf = (region: string, brand: string) => (x: { platform: string; brand_id: string; region: string }) =>
    (region === "all" || x.region === region) && (brand === "all" || x.brand_id === brand) && (!channelKey || x.platform === channelKey);
  const inScope = (reachRows: ReachRow[], region: string, brand: string) =>
    reachRows.filter((r) => r.activity === activity && scopeOf(region, brand)(r));
  const factsIn = (region: string, brand: string) =>
    (facts.data ?? []).filter((x) => inActivity(activity, x.activity_type) && scopeOf(region, brand)(x));
  const region = opts.allMarkets ? "all" : f.region;
  const parts = inScope(reach.data?.rows ?? [], region, f.brand);
  const partFacts = factsIn(region, f.brand);

  const keyOf = (dim: ReachDim, r: { platform: string; brand_id: string; region: string }) =>
    dim === "platform" ? r.platform : dim === "brand" ? brandKey.get(r.brand_id) ?? r.brand_id : dim === "region" ? r.region : "total";

  return {
    /** True until both the platforms' reach and the stored counters are in. */
    loading: !ready && !reach.isError && !facts.isError,
    error: reach.isError || facts.isError,
    fetchedAt: reach.data?.fetched_at,
    /** Aggregate for one group (key = platform key, brand key or market code), or the total. */
    get(dim: ReachDim = "total", key = "total"): ReachValue | undefined {
      if (!ready) return undefined;
      return aggregate(parts.filter((p) => keyOf(dim, p) === key), partFacts.filter((x) => keyOf(dim, x) === key));
    },
    /** Total for an explicit market and brand ("all" for any), e.g. a benchmark scoped to one. */
    scoped(region: string, brand: string): ReachValue | undefined {
      if (!ready) return undefined;
      return aggregate(inScope(reach.data?.rows ?? [], region, brand), factsIn(region, brand));
    },
  };
}
